import { FunctionComponent, useState } from "react";
import {
    Box,
    Header,
    Table,
    SpaceBetween,
    Button,
    PropertyFilter,
    Select,
    SelectProps,
    Modal,
    Pagination,
    CollectionPreferences,
} from "@amzn/awsui-components-react";
import { useFlashBarItemsActions } from "common/UseFlashBarItems/useFlashBarItems";
import { getString, getPropertyFilterString } from "common/ui-string-labels/ui-strings-utils";
import { buildColumnDefinitions, buildVisibleContentOptions, defaultPagingOptions } from "common/utils/table-utils";
import { describeError } from "common/utils/error-utils";
import { useEditableRow } from "common/utils/editable-row";
import {
    BulkOrderSource,
    EMPTY_ROW,
    VENDOR_OPTIONS,
    STATUS_OPTIONS,
    FILTERING_PROPERTIES,
    BULK_VENDOR_OPTIONS,
} from "./interfaces";
import { BulkOrdersTemplatesService } from "services/bulk-orders-templates-service";
import { useBulkOrdersTemplates } from "hooks/useBulkOrdersTemplates";

const PREFERENCES_KEY = "BulkOrdersTemplatesPreferences";

interface ActionsCellProps {
    item: BulkOrderSource;
    editingId: string | null;
    saving: boolean;
    onSave: () => void;
    onCancel: () => void;
    onEdit: (item: BulkOrderSource) => void;
    onDeactivate: (item: BulkOrderSource) => void;
    onReactivate: (item: BulkOrderSource) => void;
}

export const ActionsCell: FunctionComponent<ActionsCellProps> = ({
    item,
    editingId,
    saving,
    onSave,
    onCancel,
    onEdit,
    onDeactivate,
    onReactivate,
}) => {
    if (editingId === item.taxonomyId) {
        return (
            <SpaceBetween direction="horizontal" size="xs">
                <Button data-testid="template-row-save" onClick={onSave} loading={saving} disabled={saving}>{getString("bulkOrders.actions.save")}</Button>
                <Button data-testid="template-row-cancel" variant="link" onClick={onCancel} disabled={saving}>
                    {getString("bulkOrders.actions.cancel")}
                </Button>
            </SpaceBetween>
        );
    }
    if (!item.isActive) {
        return <Button data-testid="template-row-reactivate" onClick={() => onReactivate(item)} disabled={editingId !== null}>{getString("bulkOrders.actions.reactivate")}</Button>;
    }
    return (
        <SpaceBetween direction="horizontal" size="xs">
            <Button data-testid="template-row-edit" onClick={() => onEdit(item)} disabled={editingId !== null}>
                {getString("bulkOrders.actions.edit")}
            </Button>
            <Button data-testid="template-row-deactivate" onClick={() => onDeactivate(item)} disabled={editingId !== null}>
                {getString("bulkOrders.actions.deactivate")}
            </Button>
        </SpaceBetween>
    );
};

export interface BulkOrdersTemplatesTableProps {
    useFlashBarItemsActions: useFlashBarItemsActions;
}


export const BulkOrdersTemplatesTable: FunctionComponent<BulkOrdersTemplatesTableProps> = ({
    useFlashBarItemsActions,
}) => {
    const [selectedVendor, setSelectedVendor] = useState<SelectProps.Option>(VENDOR_OPTIONS[0]);
    const [selectedStatus, setSelectedStatus] = useState<SelectProps.Option>(STATUS_OPTIONS[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [confirmAction, setConfirmAction] = useState<{
        item: BulkOrderSource;
        action: "deactivate" | "reactivate";
    } | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const {
        allItems,
        loading,
        filterQuery,
        setFilterQuery,
        refresh,
        currentPreferences,
        onPreferencesConfirmHandler,
        columns,
    } = useBulkOrdersTemplates({
        preferencesKey: PREFERENCES_KEY,
        vendor: selectedVendor.value || undefined,
        status: selectedStatus.value ? selectedStatus.value === "true" : undefined,
        onError: (error) => useFlashBarItemsActions.addErrorFlashBarItem(describeError(error, "bulkOrders.flashbar.error")),
    });

    const pageSize = currentPreferences.pageSize!;
    const totalPages = Math.max(1, Math.ceil(allItems.length / pageSize));
    const effectivePage = Math.min(currentPage, totalPages);
    const items = allItems.slice((effectivePage - 1) * pageSize, effectivePage * pageSize);

    const VISIBLE_CONTENT_COLUMN_OPTIONS = buildVisibleContentOptions(columns);

    const { editingId, editValues, isAddingNew, editConfig, actions: editActions } = useEditableRow();

    const startNew = () => editActions.startNew(EMPTY_ROW);

    const saveRow = () => {
        // Validate required fields
        const requiredFields = columns.filter((c) => c.required);
        for (const field of requiredFields) {
            if (!editValues[field.id]?.toString().trim()) {
                useFlashBarItemsActions.addErrorFlashBarItem(
                    getString("bulkOrders.flashbar.requiredField", { field: field.header }),
                );
                return;
            }
        }

        setSaving(true);
        if (isAddingNew) {
            BulkOrdersTemplatesService.instance
                .createSource({
                    softwareName: editValues.softwareName,
                    sku: editValues.sku,
                    vendor: editValues.vendor,
                    type: editValues.type || "STANDARD",
                    cost: editValues.cost != null && editValues.cost !== "" ? Number(editValues.cost) : undefined,
                    quantity: editValues.quantity != null && editValues.quantity !== "" ? Number(editValues.quantity) : undefined,
                    costCenter: editValues.costCenter?.trim() || undefined,
                })
                .then((created) => {
                    useFlashBarItemsActions.addSuccessFlashBarItem(
                        getString("bulkOrders.flashbar.createSuccess", { name: created.softwareName }),
                    );
                    editActions.cancel();
                    refresh();
                })
                .catch((err: unknown) => {
                    console.error(err);
                    useFlashBarItemsActions.addErrorFlashBarItem(describeError(err, "bulkOrders.flashbar.error"));
                })
                .finally(() => setSaving(false));
        } else {
            if (!editingId) {
                setSaving(false);
                return;
            }
            BulkOrdersTemplatesService.instance
                .updateSource(editingId, {
                    softwareName: editValues.softwareName,
                    type: editValues.type,
                    cost: editValues.cost != null && editValues.cost !== "" ? Number(editValues.cost) : undefined,
                    quantity: editValues.quantity != null && editValues.quantity !== "" ? Number(editValues.quantity) : undefined,
                    costCenter: editValues.costCenter?.trim() || undefined,
                })
                .then((updated) => {
                    useFlashBarItemsActions.addSuccessFlashBarItem(
                        getString("bulkOrders.flashbar.updateSuccess", { name: updated.softwareName }),
                    );
                    editActions.cancel();
                    refresh();
                })
                .catch((err: unknown) => {
                    console.error(err);
                    useFlashBarItemsActions.addErrorFlashBarItem(describeError(err, "bulkOrders.flashbar.error"));
                })
                .finally(() => setSaving(false));
        }
    };

    const confirmModalAction = () => {
        if (!confirmAction) return;
        const { item, action } = confirmAction;
        const isActive = action === "reactivate";
        setConfirmLoading(true);
        BulkOrdersTemplatesService.instance
            .updateSource(item.taxonomyId, { isActive })
            .then(() => {
                useFlashBarItemsActions.addSuccessFlashBarItem(
                    getString(`bulkOrders.flashbar.${action}Success`, { name: item.softwareName }),
                );
                refresh();
            })
            .catch((err: unknown) => {
                console.error(err);
                useFlashBarItemsActions.addErrorFlashBarItem(describeError(err, "bulkOrders.flashbar.error"));
            })
            .finally(() => {
                setConfirmLoading(false);
                setConfirmAction(null);
            });
    };

    // Filtered items
    // Items come pre-filtered from the service
    const tableItems = isAddingNew
        ? [
              {
                  taxonomyId: "__new__",
                  type: "STANDARD" as const,
                  ...(editValues as any),
                  updatedAt: "",
                  updatedBy: "",
                  isActive: true,
                  createdBy: "",
                  createdAt: "",
              } as BulkOrderSource,
              ...items,
          ]
        : items;

    // Build Table columns
    const columnDefinitions = buildColumnDefinitions(
        isAddingNew
            ? columns.map((c) => {
                  if (c.id === "sku") return { ...c, editable: true };
                  if (c.id === "vendor") return { ...c, editable: true, editOptions: BULK_VENDOR_OPTIONS };
                  return c;
              })
            : columns,
        currentPreferences.visibleContent,
        { ...editConfig, idField: "taxonomyId" },
    );

    const actionsColumn = {
        id: "actions",
        header: getString("bulkOrders.columns.actions"),
        minWidth: 280,
        cell: (item: BulkOrderSource) => (
            <ActionsCell
                item={item}
                editingId={editingId}
                saving={saving}
                onSave={saveRow}
                onCancel={editActions.cancel}
                onEdit={(i) => editActions.startEdit(i.taxonomyId, i)}
                onDeactivate={(i) => setConfirmAction({ item: i, action: "deactivate" })}
                onReactivate={(i) => setConfirmAction({ item: i, action: "reactivate" })}
            />
        ),
    };

    return (
        <div style={{ padding: "0 24px" }}>
            <Table<BulkOrderSource>
                data-testid="bulk-orders-templates-table"
                variant="full-page"
                resizableColumns
                wrapLines
                header={
                    <Header
                        variant="h1"
                        
                        actions={
                            <Button data-testid="bulk-orders-templates-add-button" variant="primary" onClick={startNew} disabled={editingId !== null}>
                                {getString("bulkOrders.table.addEntryButton")}
                            </Button>
                        }
                    >
                        {getString("bulkOrders.table.templatesTitle")}
                    </Header>
                }
                filter={
                    <SpaceBetween direction="horizontal" size="s">
                        <div data-testid="bulk-orders-templates-property-filter" style={{ width: "500px" }}>
                            <PropertyFilter
                                query={filterQuery}
                                onChange={({ detail }) => {
                                    setFilterQuery(detail);
                                    setCurrentPage(1);
                                }}
                                filteringProperties={FILTERING_PROPERTIES}
                                filteringPlaceholder={getString("bulkOrders.table.filterPlaceholder")}
                                i18nStrings={getPropertyFilterString()}
                                expandToViewport
                            />
                        </div>
                        <Select
                            selectedOption={selectedVendor}
                            onChange={({ detail }) => {
                                setSelectedVendor(detail.selectedOption);
                                setCurrentPage(1);
                            }}
                            options={VENDOR_OPTIONS}
                            selectedAriaLabel="Selected"
                            ariaLabel={getString("bulkOrders.filters.vendorFilter")}
                        />
                        <Select
                            selectedOption={selectedStatus}
                            onChange={({ detail }) => {
                                setSelectedStatus(detail.selectedOption);
                                setCurrentPage(1);
                            }}
                            options={STATUS_OPTIONS}
                            selectedAriaLabel="Selected"
                            ariaLabel={getString("bulkOrders.filters.statusFilter")}
                        />
                    </SpaceBetween>
                }
                columnDefinitions={[...columnDefinitions, actionsColumn]}
                items={tableItems}
                loading={loading}
                stripedRows={currentPreferences.stripedRows}
                pagination={
                    <Pagination
                        currentPageIndex={effectivePage}
                        pagesCount={totalPages}
                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                    />
                }
                preferences={
                    <CollectionPreferences
                        title={getString("common.preferences.title")}
                        confirmLabel={getString("common.preferences.confirmLabel")}
                        cancelLabel={getString("common.preferences.cancelLabel")}
                        preferences={currentPreferences}
                        onConfirm={onPreferencesConfirmHandler}
                        pageSizePreference={{
                            title: getString("common.preferences.pageSize.title"),
                            options: defaultPagingOptions,
                        }}
                        stripedRowsPreference={{
                            label: getString("common.preferences.stripedRows.label"),
                            description: getString("common.preferences.stripedRows.description"),
                        }}
                        visibleContentPreference={{
                            title: getString("common.preferences.visibleContentPreference.title"),
                            options: [
                                {
                                    label: getString("common.preferences.visibleContentPreference.columns"),
                                    options: VISIBLE_CONTENT_COLUMN_OPTIONS,
                                },
                            ],
                        }}
                    />
                }
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>{getString("bulkOrders.table.emptyTitle")}</b>
                        <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                            {getString("bulkOrders.table.emptyMessage")}
                        </Box>
                    </Box>
                }
                loadingText={getString("bulkOrders.table.loadingText")}
            />
            {confirmAction && (
                <Modal
                    data-testid="bulk-orders-templates-confirm-modal"
                    visible
                    onDismiss={() => setConfirmAction(null)}
                    header={getString(`bulkOrders.modal.${confirmAction.action}Header`)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button data-testid="bulk-orders-templates-confirm-cancel" variant="link" onClick={() => setConfirmAction(null)} disabled={confirmLoading}>
                                    {getString("bulkOrders.actions.cancel")}
                                </Button>
                                <Button data-testid="bulk-orders-templates-confirm-action" variant="primary" onClick={confirmModalAction} loading={confirmLoading} disabled={confirmLoading}>
                                    {getString(`bulkOrders.actions.${confirmAction.action}`)}
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    {getString("bulkOrders.modal.confirmMessage", {
                        action: confirmAction.action,
                        name: confirmAction.item.softwareName,
                    })}
                </Modal>
            )}
        </div>
    );
};
